import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { InstagramLogo, FacebookLogo, WhatsappLogo, PaperPlaneTilt, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { CONTACT_EMAIL, MAILTO, ADDRESSES, SOCIAL } from "@/config/siteInfo";

export default function Footer() {
  const { t } = useLang();
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! See you Friday.");
      setEmail("");
    } catch { toast.error("Please try again."); }
  };

  return (
    <footer className="dark-section relative overflow-hidden" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_ecommerce-veloteka/artifacts/3afh4xkv_logo%20veloteka.png" alt="VELOTEKA" className="h-14 w-14 object-contain bg-white/5 p-1 rounded-sm" />
            <span className="font-display font-black text-3xl">VELOTEKA</span>
          </div>
          <p className="text-neutral-400 text-sm mb-6 max-w-md">
            Premium bikes and components imported from Germany. Delivered across the Balkans and Europe. Since day one, ride further.
          </p>

          {/* Contact block — sourced from /app/frontend/src/config/siteInfo.js */}
          <div className="space-y-2 mb-6 text-sm text-neutral-300" data-testid="footer-contact">
            <a href={MAILTO} className="flex items-center gap-2 hover:text-white" data-testid="footer-email">
              <EnvelopeSimple size={14} weight="duotone" className="text-[color:var(--vt-orange)]" /> {CONTACT_EMAIL}
            </a>
            {ADDRESSES.map((a) => (
              <div key={a.label} className="flex items-start gap-2 text-neutral-400" data-testid={`footer-address-${a.label.toLowerCase()}`}>
                <MapPin size={14} weight="duotone" className="text-[color:var(--vt-orange)] mt-1 shrink-0" />
                <span>{a.line1}, {a.line2}</span>
              </div>
            ))}
          </div>

          <form onSubmit={subscribe} className="flex gap-2 max-w-sm" data-testid="footer-newsletter-form">
            <input
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 bg-white/5 border border-white/10 px-3 py-3 text-sm text-white outline-none focus:border-[color:var(--vt-orange)]"
              data-testid="footer-newsletter-input"
            />
            <button className="vt-btn-orange" data-testid="footer-newsletter-submit">
              <PaperPlaneTilt size={18} weight="bold" />
            </button>
          </form>
          <div className="flex gap-4 mt-6">
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-ig" className="text-white hover:text-[color:var(--vt-orange)] transition-colors"><InstagramLogo size={22} /></a>
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-fb" className="text-white hover:text-[color:var(--vt-orange)] transition-colors"><FacebookLogo size={22} /></a>
            <a href={SOCIAL.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="social-wa" className="text-white hover:text-[color:var(--vt-orange)] transition-colors"><WhatsappLogo size={22} /></a>
          </div>
        </div>

        <div>
          <h4 className="overline text-white mb-4">{t.footer.shop}</h4>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li><Link to="/shop/new-bikes" className="hover:text-white">{t.nav.newBikes}</Link></li>
            <li><Link to="/shop/e-bikes" className="hover:text-white">{t.nav.eBikes}</Link></li>
            <li><Link to="/shop/used-bikes" className="hover:text-white">{t.nav.usedBikes}</Link></li>
            <li><Link to="/shop/parts" className="hover:text-white">{t.nav.parts}</Link></li>
            <li><Link to="/shop/accessories" className="hover:text-white">{t.nav.accessories}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="overline text-white mb-4">{t.footer.company}</h4>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li><Link to="/service" className="hover:text-white">{t.nav.service}</Link></li>
            <li><Link to="/rental" className="hover:text-white">{t.nav.rental}</Link></li>
            <li><Link to="/brands" className="hover:text-white">{t.nav.brands}</Link></li>
            <li><Link to="/blog" className="hover:text-white">{t.nav.blog}</Link></li>
            <li><Link to="/contact" className="hover:text-white">{t.nav.contact}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="overline text-white mb-4">{t.footer.support}</h4>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li><a href="#" className="hover:text-white">{t.footer.shipping}</a></li>
            <li><a href="#" className="hover:text-white">{t.footer.returns}</a></li>
            <li><a href="#" className="hover:text-white">{t.footer.warranty}</a></li>
            <li><a href="#" className="hover:text-white">{t.footer.terms}</a></li>
            <li><a href="#" className="hover:text-white">{t.footer.privacy}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 px-6 lg:px-12 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} VELOTEKA · Strumica, Macedonia · Deggendorf, Germany · {t.footer.rights}
      </div>
    </footer>
  );
}
